// @ts-check

/**
 * @typedef {Object} PadVisit
 * @property {number} id
 * @property {number} totalAmount
 * @property {number|null} newTotalAmount // TODO renomear para "remainingTotalAmount"
 * @property {number|null} maxTimeDifferenceMinutes
 * @property {string|null} shiftStartTime
 * @property {boolean} [isShiftDurationMissing] Plantão sem duração configurada (back-end #3368)
 * @property {{ CBOCode?: string|number }|null} [profession]
 * @property {number} [frequencyId]
 * @property {number} [frequencyAmount]
 * @property {PadPrescription[]} padPrescriptions // Opção: array de prescrições associadas a este PAD Visit. Opcional.
 */

/** @typedef {import('./buildPadPrescriptionEvent').DtEvent} DtEvent */
/** @typedef {import('./buildPadPrescriptionEvent').PadPrescription} PadPrescription */
/** @typedef {import('../calendarVisitSchedule.schema').PrescriptionLoadSchedule} PrescriptionLoadSchedule */

import DateFns from 'date-fns';
import { applyGroupEventsByTimeGapRules } from './applyGroupEventsByTimeGapRules';
import { applyScheduleDisplayRules } from './applyScheduleDisplayRules';
import { applyShiftCoverageRules } from './applyShiftCoverageRules';

/**
 * Formata uma data para o padrão DD/MM/YYYY
 * @param {Date} date - Objeto Date
 * @returns {string} - Data formatada
 */
const formatDateToDDMMYYYY = date => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

/**
 * Formata uma data para o padrão HH:mm
 * @param {Date} date - Objeto Date
 * @returns {string} - Hora formatada
 */
const formatTimeToHHMM = date => {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

/**
 * Coleta as aplicações (prescriptionCalendars) ainda sem visita vinculada como
 * eventos de data/hora.
 * @param {Array<PadPrescription>} padPrescriptions Array de prescrições
 * @returns {DtEvent[]} Eventos de data/hora com padPrescriptionId e padPrescription
 */
export const collectPadPrescriptionCalendarEvents = padPrescriptions => {
  /** @type {DtEvent[]} */
  const allDateTimePrescriptionEvents = [];

  (padPrescriptions || []).forEach(padPrescription => {
    const { id: padPrescriptionId, prescriptionContent = {} } = padPrescription;
    const { prescriptionCalendars = [] } = prescriptionContent;

    prescriptionCalendars
      .filter(({ administrateDate }) => DateFns.isValid(new Date(administrateDate)))
      .filter(({ administrateVisitationId }) => !administrateVisitationId)
      .forEach(prescriptionCalendar => {
        const { administrateDate } = prescriptionCalendar;
        const administrateDateDateTime = new Date(administrateDate);
        const date = formatDateToDDMMYYYY(administrateDateDateTime);
        const time = formatTimeToHHMM(administrateDateDateTime);

        allDateTimePrescriptionEvents.push({ date, time, padPrescriptionId, padPrescription });
      });
  });

  return allDateTimePrescriptionEvents;
};

/**
 * Transforma prescriptionCalendars em um agendamento de visita
 * @param {Array<PadPrescription>} padPrescriptions Array de prescrições
 * @param {PadVisit} padVisit Objeto da visita
 * @returns {Array<PrescriptionLoadSchedule>} Array de itens de agendamento de visita
 */
export const transformPadPrescriptionsToPrescriptionLoadSchedule = (padPrescriptions, padVisit) => {
  const { totalAmount, maxTimeDifferenceMinutes, shiftStartTime } = padVisit;

  // 1. Coleta as aplicações sem visita vinculada
  const allDateTimePrescriptionEvents = collectPadPrescriptionCalendarEvents(padPrescriptions);

  // 2. Mantém só os eventos da janela contratada do plantão; os demais ficam no
  // limbo (regra completa em applyShiftCoverageRules.js, #2205)
  const { coveredEvents } = applyShiftCoverageRules(allDateTimePrescriptionEvents, padVisit);

  // 3. Agrupa os eventos pelo gap de tempo
  const scheduleMap = applyGroupEventsByTimeGapRules({
    dateTimeEvents: coveredEvents,
    maxTimeDifferenceMinutes,
    shiftStartTime
  });

  const scheduleList = Array.from(scheduleMap.values());

  // 4. Aplica as regras de negócio de exibição/limites e retorna
  const transformedSchedule = applyScheduleDisplayRules(scheduleList, totalAmount);

  return transformedSchedule;
};
